import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, BellOff, Plus, Trash2, Smartphone, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { BIST_QUICK } from "@/lib/quickSymbols";
import { pushSupported, subscribeToPush, unsubscribeFromPush, getCurrentSubscription, sendTestPush } from "@/lib/push";

const KIND_OPTIONS = [
  { id: "price_gt",       label: "Fiyat >",             unit: "₺" },
  { id: "price_lt",       label: "Fiyat <",             unit: "₺" },
  { id: "rsi_gt",         label: "RSI >",               unit: "" },
  { id: "rsi_lt",         label: "RSI <",               unit: "" },
  { id: "pct_change_gt",  label: "Günlük % Değişim >",  unit: "%" },
  { id: "pct_change_lt",  label: "Günlük % Değişim <",  unit: "%" },
];

export default function AlertsRulesPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/alerts/rules"); setRules(data.items || []); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    (async () => {
      try {
        const sub = await getCurrentSubscription();
        setPushOn(!!sub && Notification.permission === "granted");
      } catch {}
    })();
  }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/alerts/rules/${id}`);
      setRules((p) => p.filter((r) => r.id !== id));
      toast.success("Kural silindi");
    } catch { toast.error("Silinemedi"); }
  };

  const enablePush = async () => {
    if (!pushSupported()) { toast.error("Bu tarayıcı push desteklemiyor"); return; }
    setPushBusy(true);
    try {
      await subscribeToPush();
      setPushOn(true);
      toast.success("Push bildirim açıldı 📱");
    } catch (e) { toast.error(e.message || "Push açılamadı"); }
    finally { setPushBusy(false); }
  };
  const disablePush = async () => {
    setPushBusy(true);
    try { await unsubscribeFromPush(); setPushOn(false); toast.success("Push kapatıldı"); }
    catch { toast.error("Kapatılamadı"); }
    finally { setPushBusy(false); }
  };
  const testPush = async () => {
    try { await sendTestPush(); toast.success("Test bildirimi gönderildi"); }
    catch (e) { toast.error(e.response?.data?.detail || "Test başarısız"); }
  };

  return (
    <div className="space-y-6" data-testid="alerts-rules-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary ztan-title flex items-center gap-2">
            <Bell size={22} className="text-brand-blue" /> Alarm Kuralları
          </h1>
          <p className="text-sm text-text-secondary mt-1">Fiyat, RSI ve % değişim eşik alarmları — hem uygulamada hem telefonda</p>
        </div>
        <button
          data-testid="alert-rule-add-btn"
          onClick={() => setShowAdd(true)}
          className="bg-accent-yellow text-bg-base font-semibold px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 hover:opacity-90"
        ><Plus size={14} /> Yeni Alarm</button>
      </div>

      {/* Push status card */}
      <div
        data-testid="push-status-card"
        className="bg-bg-panel border border-bg-border rounded-xl p-4 flex flex-wrap items-center gap-4"
      >
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${pushOn ? "bg-signal-up/15 text-signal-up border border-signal-up/30" : "bg-bg-hover text-text-secondary border border-bg-border"}`}>
          {pushOn ? <BellRing size={16} /> : <BellOff size={16} />}
        </div>
        <div className="flex-1 min-w-[180px]">
          <div className="text-sm font-semibold text-text-primary flex items-center gap-2">
            Telefon Push Bildirimi
            {pushOn && <ShieldCheck size={12} className="text-signal-up" />}
          </div>
          <div className="text-xs text-text-secondary mt-0.5">
            {pushOn
              ? "Aktif — alarm tetiklendiğinde telefonunuza bildirim gelir."
              : "Kapalı — açarsanız site kapalıyken bile telefonunuza bildirim gönderebiliriz."}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {pushOn ? (
            <>
              <button data-testid="push-test-btn" onClick={testPush} className="bg-bg-hover text-text-primary border border-bg-border text-xs px-3 py-2 rounded-lg hover:bg-bg-border flex items-center gap-1.5">
                <Smartphone size={12} /> Test Gönder
              </button>
              <button data-testid="push-disable-btn" onClick={disablePush} disabled={pushBusy} className="text-signal-down border border-signal-down/30 bg-signal-down/10 text-xs px-3 py-2 rounded-lg hover:bg-signal-down/20 disabled:opacity-60">
                Kapat
              </button>
            </>
          ) : (
            <button data-testid="push-enable-btn" onClick={enablePush} disabled={pushBusy} className="bg-brand-blue text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-brand-blue/90 disabled:opacity-60 flex items-center gap-1.5">
              <Smartphone size={12} /> {pushBusy ? "Açılıyor..." : "Push Bildirimlerini Aç"}
            </button>
          )}
        </div>
      </div>

      {/* Rules list */}
      {loading ? (
        <div className="p-16 flex justify-center"><div className="radar-loader" /></div>
      ) : rules.length === 0 ? (
        <div className="bg-bg-panel border border-bg-border rounded-xl p-16 text-center">
          <Bell size={26} className="text-text-secondary mx-auto mb-2 opacity-60" />
          <h3 className="text-text-primary font-semibold mb-1">Henüz alarm kuralı yok</h3>
          <p className="text-sm text-text-secondary">"Yeni Alarm" butonu ile eşik tabanlı bildirim tanımlayın.</p>
        </div>
      ) : (
        <div className="bg-bg-panel border border-bg-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-text-secondary text-xs uppercase tracking-wider border-b border-bg-border">
                <th className="py-3 px-4 text-left">Hisse</th>
                <th className="py-3 px-4 text-left">Kural</th>
                <th className="py-3 px-4 text-left">Not</th>
                <th className="py-3 px-4 text-center">Durum</th>
                <th className="py-3 px-4 text-center">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => {
                const opt = KIND_OPTIONS.find((k) => k.id === r.kind);
                return (
                  <tr key={r.id} data-testid={`rule-row-${r.id}`} className="border-b border-bg-border hover:bg-bg-hover">
                    <td className="py-3 px-4">
                      <button onClick={() => nav(`/stock/${r.symbol}`)} className="text-left">
                        <div className="font-mono font-bold text-text-primary">{r.symbol}</div>
                        <div className="text-[10px] text-text-secondary">{r.name}</div>
                      </button>
                    </td>
                    <td className="py-3 px-4 text-text-primary">
                      <span className="font-mono">{opt?.label || r.kind} {opt?.unit === "₺" ? "₺" : ""}{r.threshold}{opt?.unit === "%" ? "%" : ""}</span>
                    </td>
                    <td className="py-3 px-4 text-text-secondary text-xs">{r.note || "-"}</td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-[10px] px-2 py-0.5 rounded border text-signal-up bg-signal-up/10 border-signal-up/30">Aktif</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button data-testid={`rule-delete-${r.id}`} onClick={() => remove(r.id)} className="text-text-secondary hover:text-signal-down">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && <AddRuleModal onClose={() => setShowAdd(false)} onSaved={(r) => { setRules([r, ...rules]); setShowAdd(false); }} />}
    </div>
  );
}

function AddRuleModal({ onClose, onSaved }) {
  const [symbol, setSymbol] = useState("AKBNK");
  const [kind, setKind] = useState("price_gt");
  const [threshold, setThreshold] = useState(100);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const opt = KIND_OPTIONS.find((k) => k.id === kind);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/alerts/rules", { symbol, kind, threshold: Number(threshold), note });
      toast.success("Alarm oluşturuldu");
      onSaved(data);
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Oluşturulamadı");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form data-testid="add-rule-modal" onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-bg-panel border border-bg-border rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-bg-border">
          <h3 className="text-lg font-semibold text-text-primary">Yeni Alarm Kuralı</h3>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-text-secondary mb-1.5">Hisse</label>
            <input data-testid="rule-symbol-input" list="ztan-rule-symbols" value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              className="w-full bg-bg-base border border-bg-border text-text-primary font-mono rounded-lg px-3 py-2.5 text-sm focus:border-brand-blue focus:outline-none" />
            <datalist id="ztan-rule-symbols">{BIST_QUICK.map((s) => <option key={s} value={s} />)}</datalist>
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1.5">Koşul</label>
            <select data-testid="rule-kind-select" value={kind} onChange={(e) => setKind(e.target.value)}
              className="w-full bg-bg-base border border-bg-border text-text-primary rounded-lg px-3 py-2.5 text-sm focus:border-brand-blue focus:outline-none">
              {KIND_OPTIONS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1.5">Eşik değer ({opt?.unit || ""})</label>
            <input data-testid="rule-threshold-input" type="number" step="0.01" value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className="w-full bg-bg-base border border-bg-border text-text-primary font-mono rounded-lg px-3 py-2.5 text-sm focus:border-brand-blue focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1.5">Not (opsiyonel)</label>
            <input data-testid="rule-note-input" value={note} onChange={(e) => setNote(e.target.value)}
              className="w-full bg-bg-base border border-bg-border text-text-primary rounded-lg px-3 py-2.5 text-sm focus:border-brand-blue focus:outline-none" />
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button type="button" onClick={onClose} className="text-text-secondary text-sm px-3 py-2">İptal</button>
            <button data-testid="rule-save-btn" type="submit" disabled={busy}
              className="bg-brand-blue text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-brand-blue/90 disabled:opacity-60">
              {busy ? "Kaydediliyor..." : "Alarmı Kaydet"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
